import { cn } from "@base/lib/utils";
import type { ComponentProps, KeyboardEvent } from "react";

// 좁은 화면에서 컬럼이 잘리지 않도록 가로 스크롤 컨테이너로 감싼다.
const TableRoot = ({ className, ...rest }: ComponentProps<"table">) => (
  <div className="overflow-x-auto w-full bg-white border border-gray-300 rounded-8">
    <table className={cn("w-full border-collapse", className)} {...rest} />
  </div>
);

const Head = ({ className, ...rest }: ComponentProps<"thead">) => (
  <thead className={cn("bg-gray-100", className)} {...rest} />
);

const Body = (props: ComponentProps<"tbody">) => <tbody {...props} />;

// 행 전체를 클릭 대상으로 쓰는 경우 tr에는 기본 키보드 접근성이 없으므로
// role과 tabIndex를 부여하고 Enter/Space를 클릭으로 연결한다.
const Row = ({ className, onClick, ...rest }: ComponentProps<"tr">) => {
  const isClickable = !!onClick;

  const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <tr
      className={cn(
        "border-t border-gray-200 even:bg-gray-10",
        isClickable && "cursor-pointer hover:bg-gray-100",
        className,
      )}
      onClick={onClick}
      {...(isClickable ? { role: "button", tabIndex: 0, onKeyDown: handleKeyDown } : {})}
      {...rest}
    />
  );
};

const HeaderCell = ({ className, ...rest }: ComponentProps<"th">) => (
  <th
    className={cn("px-4 py-3 font-semibold text-left whitespace-nowrap text-gray-900 text-label2", className)}
    {...rest}
  />
);

const Cell = ({ className, ...rest }: ComponentProps<"td">) => (
  <td className={cn("px-4 py-3 whitespace-nowrap text-gray-1000 text-body4", className)} {...rest} />
);

export const Table = Object.assign(TableRoot, {
  Head,
  Body,
  Row,
  HeaderCell,
  Cell,
});
